/**
 * Country detection utilities
 * Used to determine user's country for language selection
 */

/**
 * Checks if a timezone is from Brazil
 * Brazil uses America/Sao_Paulo, America/Manaus, America/Belem, etc.
 */
export const isBrazilianTimezone = (timezone: string): boolean => {
  const brazilianTimezones = [
    'America/Sao_Paulo',
    'America/Manaus',
    'America/Belem',
    'America/Fortaleza',
    'America/Recife',
    'America/Araguaina',
    'America/Maceio',
    'America/Bahia',
    'America/Campo_Grande',
    'America/Cuiaba',
    'America/Porto_Velho',
    'America/Boa_Vista',
    'America/Rio_Branco',
    'America/Noronha',
  ];
  
  return brazilianTimezones.includes(timezone);
};

/**
 * Detects if user is from Brazil based on timezone
 * This is a fallback when country field is not available
 */
export const detectCountryFromTimezone = (timezone: string): 'BR' | 'OTHER' => {
  return isBrazilianTimezone(timezone) ? 'BR' : 'OTHER';
};

/**
 * Gets user's country from profile or detects from timezone
 * Returns 'BR' for Brazil, 'OTHER' for other countries
 */
export const getUserCountry = async (userId: string | undefined): Promise<'BR' | 'OTHER'> => {
  if (!userId) {
    // If no user, detect from browser timezone
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return detectCountryFromTimezone(browserTimezone);
  }

  try {
    const { supabase } = await import('@/integrations/supabase/client');
    
    // First, try to get country from profile
    const { data: profile, error } = await supabase
      .from('profiles')
      .select('country, timezone')
      .eq('id', userId)
      .single();

    if (error) {
      console.warn('[getUserCountry] Error fetching profile:', error);
      // Fallback to timezone detection
      const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return detectCountryFromTimezone(browserTimezone);
    }

    // If country is explicitly set to Brazil, return BR
    if (profile?.country && profile.country.toLowerCase() === 'brazil') {
      return 'BR';
    }

    // If country is set but not Brazil, return OTHER
    if (profile?.country) {
      return 'OTHER';
    }

    // If no country set, detect from timezone (profile or browser)
    const timezone = profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    return detectCountryFromTimezone(timezone);
  } catch (error) {
    console.warn('[getUserCountry] Error:', error);
    // Fallback to browser timezone
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return detectCountryFromTimezone(browserTimezone);
  }
};
